import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { useIssue, useZoneMe } from '../../features/zone/api.ts';
import { DeleteIssueButton } from '../../features/zone/DeleteIssueButton.tsx';
import { IssueActions } from '../../features/zone/IssueActions.tsx';
import { IssueTimeline } from '../../features/zone/IssueTimeline.tsx';
import { PhotoGrid } from '../../features/zone/PhotoGrid.tsx';
import { PriorityMark } from '../../features/zone/PriorityMark.tsx';
import { StatusChip } from '../../features/zone/StatusChip.tsx';
import { absoluteTime, relativeTime } from '../../features/zone/time.ts';
import { useAuthStore } from '../../store/auth.ts';

function Detail({ icon, label, children }: { icon: string; label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex gap-3 py-3 first:pt-0 last:pb-0 sm:py-0">
      <Icon name={icon} size={20} className="mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 break-words text-[0.95rem] leading-snug">{children}</dd>
      </div>
    </div>
  );
}

const Muted = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className="text-muted-foreground">{children}</span>
);

export function ZoneIssueDetail(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const user = useAuthStore((s) => s.user);
  const { data: me } = useZoneMe();
  const { data: issue, isLoading, isError } = useIssue(id);

  const back = (
    <Link to="/zone" className="btn-ghost -ms-4 self-start">
      <Icon name="arrow_back" size={20} /> Issues
    </Link>
  );

  if (isLoading) return <div className="page-narrow"><p className="status-label" role="status">Loading…</p></div>;
  if (isError || !issue) {
    return (
      <div className="page-narrow space-y-4">
        {back}
        <p className="text-sm text-muted-foreground" role="alert">This issue could not be found.</p>
      </div>
    );
  }

  const hasCoords = issue.lat !== null && issue.lng !== null;
  const canAddPhotos = !!user && me?.zoneRole === 'COUNCILLOR' && issue.createdById === user.id && issue.status === 'SUBMITTED';
  const zoneRole = me?.zoneRole ?? null;
  // Mirrors backend canDelete: the creator (still a councillor) or the org admin.
  const canDelete = !!user && ((zoneRole === 'COUNCILLOR' && issue.createdById === user.id) || user.isOrgAdmin);

  const details = (
    <section className="rounded-3xl border border-[hsl(var(--border)/0.8)] p-5" aria-label="Details">
      <dl className="divide-y divide-[hsl(var(--border)/0.6)] sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-5 sm:divide-y-0">
        <Detail icon="location_on" label="Zone">{issue.zone.name}</Detail>
        <Detail icon="filter_list" label="Category">{issue.category?.name ?? <Muted>Not yet categorised</Muted>}</Detail>
        <Detail icon="assignment_ind" label="Assigned to">{issue.assignee?.name ?? <Muted>Unassigned</Muted>}</Detail>
        <Detail icon="person" label="Reported by">
          {issue.createdBy.name}
          <span className="block text-sm text-muted-foreground">{absoluteTime(issue.createdAt)}</span>
        </Detail>
        {(issue.locationText || hasCoords) && (
          <Detail icon="map" label="Location">
            {issue.locationText && <span className="block">{issue.locationText}</span>}
            {hasCoords && (
              <a
                href={`https://maps.google.com/?q=${issue.lat},${issue.lng}`}
                target="_blank"
                rel="noopener"
                className="mt-0.5 inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                Open in Maps <Icon name="open_in_new" size={16} />
              </a>
            )}
          </Detail>
        )}
      </dl>
    </section>
  );

  return (
    <div className="page">

      {/* Desktop: Planner-style — the task on the left, its activity/comments chat on the right. */}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_26rem] lg:gap-10 xl:grid-cols-[minmax(0,1fr)_30rem]">
        {/* flex gap (not space-y) so the desktop-only / phone-only blocks leave no phantom margins. */}
        <div className="flex min-w-0 flex-col gap-8 lg:gap-10">
        <header className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            {back}
            {canDelete && <DeleteIssueButton issue={issue} />}
          </div>
          <h1 className="break-words text-[1.75rem] leading-tight tracking-tight lg:text-[2.25rem]">
            <span className="text-muted-foreground">#{issue.number}</span> {issue.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <StatusChip status={issue.status} />
            <PriorityMark priority={issue.priority} />
            <span className="inline-flex items-center gap-1"><Icon name="location_on" size={16} />{issue.zone.name}</span>
            <span title={absoluteTime(issue.createdAt)}>Reported {relativeTime(issue.createdAt)}</span>
          </div>
        </header>

          {user && (
            <div className="hidden lg:block">
              <IssueActions issue={issue} userId={user.id} zoneRole={zoneRole} layout="inline" />
            </div>
          )}

          <section aria-label="Description">
            <h2 className="mb-2 text-sm font-medium text-primary">Description</h2>
            <p className="max-w-[70ch] whitespace-pre-wrap break-words text-base leading-7 lg:text-[1.0625rem] lg:leading-8">
              {issue.description}
            </p>
          </section>

          {details}

          <PhotoGrid issueId={issue.id} photos={issue.photos} canAdd={canAddPhotos} />

          {/* Phones: activity flows below, then the sticky action bar above the bottom nav. */}
          <div className="lg:hidden"><IssueTimeline issue={issue} /></div>
          {user && (
            // `contents` so the bar's `sticky` is scoped to this whole column, not a wrapper.
            <div className="contents lg:hidden">
              <IssueActions issue={issue} userId={user.id} zoneRole={zoneRole} />
            </div>
          )}
        </div>

        <aside className="hidden lg:block" aria-label="Activity panel">
          <div className="sticky top-24 h-[calc(100dvh-8rem)] min-h-[28rem]">
            <IssueTimeline issue={issue} variant="panel" />
          </div>
        </aside>
      </div>
    </div>
  );
}
